import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Checkbox } from 'antd'
import { isEmpty } from 'ramda'
import $t from '../../../i18n'
import MaterialInput from '../../common/MaterialInput'
import Actions from '../../../actions'
import { COUNTRY_CODES } from '../../../consts'
import { event, eventTypes } from '../../../consts/eventConsts'
import { formatClientSideError } from '../../../utils/eventUtils'
import Page from '../../common/Page/Page'
import PageCard from '../../common/PageCard'
import EditableItem from '../../common/EditableItem'
import USCountries from '../../../resources/us.json'
import { getIsHmoMember } from '../../../utils/hmoUtils'
import MaterialSelectWithDropDown from '../../common/MaterialSelectWithDropdown'
import VerifyPhoneCode from '../VerifyPhoneCode'

import './ContactInformation.css'

const getValidationErrors = ({
  phoneNumber,
  email,
  address,
  city,
  state,
  zipCode,
  isMailingAddress,
}) => {
  const result = {}
  const numericRegex = /^\d{10}$/

  if (!phoneNumber) {
    result.phoneNumber = $t('errors.validation.missing')
  }
  if (!phoneNumber.match(numericRegex)) {
    result.phoneNumber = $t('errors.validation.invalidPhone')
  }
  if (!email) {
    result.email = $t('errors.validation.missing')
  }
  if (!address && isMailingAddress) {
    result.address = $t('errors.validation.missing')
  }
  if (!city && isMailingAddress) {
    result.city = $t('errors.validation.missing')
  }
  if (!state && isMailingAddress) {
    result.state = $t('errors.validation.missing')
  }
  if (!zipCode && isMailingAddress) {
    result.zipCode = $t('errors.validation.missing')
  }
  return (!isEmpty(result) && result) || null
}

const ContactInformation = () => {
  const dispatch = useDispatch()
  const [inEditMode, setInEditMode] = useState(false)
  const [inVerifyCodeMode, setInVerifyCodeMode] = useState(false)
  const [isChangeInEmail, setIsChangeInEmail] = useState(false)
  const [isChangeInPhone, setIsChangeInPhone] = useState(false)
  const userDetails = useSelector(state => state.profile.userDetails)
  const isHmoMember = getIsHmoMember(userDetails)

  const owner = useSelector(state => state.account?.owner)
  const activeMembers = useSelector(state => state.account?.activeMembers)
  const isOwnerWithFamily = userDetails?.id === owner && !!activeMembers?.length
  const homeAddress = userDetails?.address?.find(
    address => address?.address_type === 'Residential'
  )
  const mailingAddressHmo = userDetails?.address?.find(
    address => address?.address_type === 'Mailing'
  )
  const [isUseHomeAdrForMailing, setIsUseHomeAdrForMailing] = useState(false)

  const isMailingAddress = !isUseHomeAdrForMailing && isHmoMember
  const currentHomeAddress = homeAddress
  const [shouldValidate, setShouldValidate] = useState(false)
  const stateList = USCountries

  const [mailingAddress, setMailingAddress] = useState(
    mailingAddressHmo?.address1
  )
  const [mailingAddress2, setMailingAddress2] = useState(
    mailingAddressHmo?.address2
  )
  const [mailingCity, setMailingCity] = useState(mailingAddressHmo?.city)
  const [mailingState, setMailingState] = useState(
    mailingAddressHmo?.state
      ? stateList?.find(
          state =>
            state?.isoCode?.toLowerCase() ===
            mailingAddressHmo?.state?.toLowerCase()
        )?.isoCode
      : null
  )
  const [mailingZipCode, setMailingZipCode] = useState(
    mailingAddressHmo?.zipCode
  )
  const [email, setEmail] = useState(userDetails?.email)

  const currentPhoneNumber = userDetails?.phoneNumber
  const [phoneNumber, setPhoneNumber] = useState({
    short: 'US',
    code: COUNTRY_CODES[0],
    phone: '',
  })

  const stateListNameValue = USCountries.map(item => {
    return { label: item?.name, value: item?.isoCode }
  })

  const isContactInformationLoading = useSelector(
    state => state.profile?.isContactInformationLoading
  )
  const updateContactInformationError = useSelector(
    state => state.profile?.updateContactInformationError
  )
  const isContactInformationUpdateSuccess = useSelector(
    state => state.profile?.isContactInformationUpdateSuccess
  )
  const isPhoneNuberToVerifyChecked = useSelector(
    state => state.profile?.isPhoneNuberToVerifyChecked
  )

  const fullHomeAddress = currentHomeAddress
    ? [
        currentHomeAddress?.address1,
        currentHomeAddress?.address2,
        currentHomeAddress?.city,
        currentHomeAddress?.state,
        currentHomeAddress?.zipCode,
      ]
        .filter(item => item)
        .join(', ')
    : ''

  const fullMailingAddress = mailingAddressHmo
    ? [
        mailingAddress,
        mailingAddress2,
        mailingCity,
        mailingState,
        mailingZipCode,
      ]
        .filter(item => item)
        .join(', ')
    : ''

  const fullPhoneNumber = `+${phoneNumber?.code}${phoneNumber?.phone}`
  const { isPhoneNumbersVerifySuccess } = useSelector(state => state.profile)

  useEffect(() => {
    if (isPhoneNumbersVerifySuccess) {
      onSubmit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhoneNumbersVerifySuccess])

  useEffect(() => {
    onResetPhone()
  }, [])

  useEffect(() => {
    if (updateContactInformationError) {
      setInEditMode(true)
      setInVerifyCodeMode(false)
    }
  }, [updateContactInformationError])

  useEffect(() => {
    if (isPhoneNuberToVerifyChecked) {
      setInEditMode(false)
      setInVerifyCodeMode(true)
    }
  }, [isPhoneNuberToVerifyChecked])

  useEffect(() => {
    if (isContactInformationUpdateSuccess) {
      onReset()
    }
  }, [isContactInformationUpdateSuccess])

  const onResetPhone = () => {
    for (const countryCode of COUNTRY_CODES) {
      if (
        currentPhoneNumber &&
        currentPhoneNumber.toString().startsWith(`+${countryCode}`)
      ) {
        const phone = currentPhoneNumber
          .toString()
          .replace(`+${countryCode}`, '')
        const code = countryCode.toString()
        setPhoneNumber(prev => ({ ...prev, phone, code }))
      }
    }
  }

  const onReset = () => {
    setInEditMode(false)
    setInVerifyCodeMode(false)
    setIsChangeInEmail(false)
    setIsChangeInPhone(false)
    setShouldValidate(false)
    setMailingAddress(mailingAddressHmo?.address1)
    setMailingAddress2(mailingAddressHmo?.address2)
    setMailingCity(mailingAddressHmo?.city)
    setMailingState(mailingAddressHmo?.state)
    setMailingZipCode(mailingAddressHmo?.zipCode)
    setEmail(userDetails?.email)
    setIsUseHomeAdrForMailing(!mailingAddressHmo)
    onResetPhone()
    dispatch(
      Actions.createAction(Actions.UPDATE_CONTACT_INFORMATION_RESET_ERROR)
    )
  }

  const onSubmit = () => {
    // remove '-' and '.' chars from number, is exists. remove spaces
    const phoneWithoutChars = phoneNumber?.phone
      ? phoneNumber?.phone
          .toString()
          .replace(new RegExp('[-.]', 'g'), '')
          .replace(/\s/g, '')
      : ''

    const payload = {
      phone: '+' + phoneNumber?.code.toString() + phoneWithoutChars,
      email: email,
      mailingAddress: mailingAddress,
      mailingAddress2: mailingAddress2,
      mailingCity: mailingCity,
      mailingState: mailingState,
      mailingZipCode: mailingZipCode,
      patientId: userDetails?.id,
    }
    if (isUseHomeAdrForMailing) {
      payload.mailingAddress = currentHomeAddress?.address1
      payload.mailingAddress2 = currentHomeAddress?.address2
      payload.mailingCity = currentHomeAddress?.city
      payload.mailingState = currentHomeAddress?.state
      payload.mailingZipCode = currentHomeAddress?.zipCode
    }
    dispatch(
      Actions.createAction(
        Actions.UPDATE_CONTACT_INFORMATION_REQUESTED,
        payload
      )
    )
  }

  const onCancel = () => {
    onReset()
  }

  const onContinue = () => {
    // remove '-' and '.' chars from number, is exists. remove spaces
    const phoneWithoutChars = phoneNumber?.phone
      ? phoneNumber?.phone
          .toString()
          .replace(new RegExp('[-.]', 'g'), '')
          .replace(/\s/g, '')
      : ''

    const payload = {
      phone: '+' + phoneNumber?.code.toString() + phoneWithoutChars,
      email: email,
      mailingAddress: mailingAddress,
      mailingAddress2: mailingAddress2,
      mailingCity: mailingCity,
      mailingState: mailingState,
      mailingZipCode: mailingZipCode,
      patientId: userDetails?.id,
    }

    const validationErrors = getValidationErrors({
      phoneNumber: phoneWithoutChars,
      email: email,
      address: mailingAddress,
      city: mailingCity,
      state: mailingState,
      zipCode: mailingZipCode,
      isMailingAddress: isMailingAddress,
    })

    if (validationErrors) {
      setShouldValidate(true)
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: payload,
          element: 'Contact Information',
          error: formatClientSideError(validationErrors),
        })
      )
      return
    }
    if (isChangeInPhone) {
      dispatch(
        Actions.createAction(Actions.PHONE_NUMBER_VERIFICATION_REQUESTED, {
          phoneNumber: fullPhoneNumber,
        })
      )
    } else {
      onSubmit()
    }
  }

  const onBack = () => {
    if (inEditMode) {
      onReset()
    } else if (inVerifyCodeMode) {
      setInVerifyCodeMode(false)
      setInEditMode(true)
    } else {
      dispatch(Actions.createAction(Actions.NAVIGATED_BACK))
    }
  }

  const onEdit = () => {
    setInEditMode(true)
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.click,
        eventName: event.clickEvent.click,
        element: 'Edit contact information',
      })
    )
  }

  const errors =
    shouldValidate &&
    getValidationErrors({
      phoneNumber: phoneNumber?.phone,
      email: email,
      address: mailingAddress,
      city: mailingCity,
      state: mailingState,
      zipCode: mailingZipCode,
      isMailingAddress: isMailingAddress,
    })

  const renderAddress = type => {
    const canEditAddress = inEditMode && type !== $t('contactInformation.home')
    return (
      <>
        <div className="address">
          <MaterialInput
            className={
              canEditAddress ? 'contact-input' : 'contact-input-disabled'
            }
            value={
              canEditAddress ? mailingAddress : currentHomeAddress?.address1
            }
            onChange={
              canEditAddress ? e => setMailingAddress(e.target.value) : null
            }
            disabled={!canEditAddress}
            errorText={canEditAddress ? errors?.address : null}
            label={$t('contactInformation.streetAddress', { type: type })}
          />
          {(canEditAddress || !!currentHomeAddress?.address2) && (
            <MaterialInput
              className={
                canEditAddress ? 'contact-input' : 'contact-input-disabled'
              }
              value={
                canEditAddress ? mailingAddress2 : currentHomeAddress?.address2
              }
              onChange={
                canEditAddress ? e => setMailingAddress2(e.target.value) : null
              }
              disabled={!canEditAddress}
              errorText={null}
              label={$t('contactInformation.streetAddress2', { type: type })}
            />
          )}
          <div className="zipcode-city-container">
            <MaterialInput
              className={
                canEditAddress ? 'contact-input' : 'contact-input-disabled'
              }
              value={
                canEditAddress ? mailingZipCode : currentHomeAddress?.zipCode
              }
              onChange={
                canEditAddress ? e => setMailingZipCode(e.target.value) : null
              }
              disabled={!canEditAddress}
              errorText={canEditAddress ? errors?.zipCode : null}
              label={$t('contactInformation.zipCode')}
            />
            <MaterialInput
              className={
                canEditAddress ? 'contact-input' : 'contact-input-disabled'
              }
              value={canEditAddress ? mailingCity : currentHomeAddress?.city}
              onChange={
                canEditAddress ? e => setMailingCity(e.target.value) : null
              }
              disabled={!canEditAddress}
              errorText={canEditAddress ? errors?.city : null}
              label={$t('contactInformation.city')}
            />
          </div>
          {canEditAddress && (
            <MaterialSelectWithDropDown
              className={
                canEditAddress ? 'contact-input' : 'contact-input-disabled'
              }
              value={mailingState}
              onChange={value => setMailingState(value)}
              disabled={!canEditAddress}
              errorText={canEditAddress ? errors?.state : null}
              data={stateListNameValue}
              label={$t('contactInformation.state')}
            />
          )}
          {!canEditAddress && (
            <MaterialInput
              className={
                canEditAddress ? 'contact-input' : 'contact-input-disabled'
              }
              value={canEditAddress ? mailingState : currentHomeAddress?.state}
              disabled={!canEditAddress}
              label={$t('contactInformation.state')}
            />
          )}
        </div>
      </>
    )
  }

  const renderContent = () => {
    return (
      <>
        {!inEditMode && (
          <EditableItem
            onEdit={() => onEdit()}
            membersIds={[userDetails?.id]}
            dataName="contact-information-edit-card"
          >
            <div className="container-editable">
              <div className="editable-item-details">
                <div className="editable-item-col">
                  <div className="contact-item">
                    <div className="title">
                      {$t('contactInformation.email')}
                    </div>
                    <div className="description">{userDetails?.email}</div>
                  </div>
                  <div className="contact-item">
                    <div className="title">
                      {$t('contactInformation.phone')}
                    </div>
                    <div className="description">
                      {userDetails?.phoneNumber}
                    </div>
                  </div>
                </div>
                <div className="editable-item-col">
                  <div className="contact-item">
                    <div className="title">
                      {$t('contactInformation.homeAddress')}
                    </div>
                    <div className="description">{fullHomeAddress}</div>
                  </div>
                  {isHmoMember && (
                    <div className="contact-item">
                      <div className="title">
                        {$t('contactInformation.mailingAddress')}
                      </div>
                      <div className="description">{fullMailingAddress}</div>
                    </div>
                  )}
                </div>
              </div>
              {isOwnerWithFamily && (
                <div className="disclaimer">
                  {isHmoMember
                    ? $t('contactInformation.disclaimerHmo')
                    : $t('contactInformation.disclaimerTh')}
                </div>
              )}
            </div>
          </EditableItem>
        )}
        {inEditMode && (
          <div>
            {renderAddress($t('contactInformation.home'))}
            {isHmoMember && (
              <div className="use-address-checkbox">
                <Checkbox
                  className="checkbox"
                  onChange={() =>
                    setIsUseHomeAdrForMailing(!isUseHomeAdrForMailing)
                  }
                  checked={isUseHomeAdrForMailing}
                />
                {$t('contactInformation.useForMailing')}
              </div>
            )}
            {isMailingAddress
              ? renderAddress($t('contactInformation.mailing'))
              : null}
            <div>
              <MaterialInput
                className="contact-input"
                value={email}
                type="email"
                errorText={errors?.email}
                onChange={e => {
                  setEmail(e.target.value)
                  setIsChangeInEmail(true)
                }}
                disabled={!inEditMode}
                label={$t('contactInformation.email')}
              />
              {isChangeInEmail && (
                <div className="note">
                  <i className="icon-info"></i>
                  {$t('contactInformation.noteEmail')}
                </div>
              )}
              <MaterialInput
                className="contact-input"
                value={phoneNumber}
                errorText={errors?.phoneNumber}
                onChange={e => {
                  setPhoneNumber(e)
                  setIsChangeInPhone(true)
                }}
                defaultValue={phoneNumber}
                disabled={!inEditMode}
                type="phone"
                label={$t('contactInformation.phone')}
              />
              {isChangeInPhone && (
                <div className="note">
                  <i className="icon-info"></i>
                  {$t('contactInformation.notePhone')}
                </div>
              )}
            </div>
            <div className="error-message">{updateContactInformationError}</div>
            <div className="page-footer">
              <Button
                key="1"
                className="button-container trackable"
                type="primary"
                data-name="save"
                onClick={onContinue}
              >
                {$t('common.save')}
              </Button>
              <Button
                key="2"
                className="button-container trackable"
                type="secondary"
                data-name="cancel"
                onClick={onCancel}
              >
                {$t('common.cancel')}
              </Button>
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <PageCard enableBack={true} onBack={onBack}>
      {!inVerifyCodeMode && (
        <Page
          hideAlertBanner={true}
          title={$t('contactInformation.title')}
          className="contact-information-page"
          loading={isContactInformationLoading}
        >
          {renderContent()}
        </Page>
      )}
      {inVerifyCodeMode && <VerifyPhoneCode onSubmit={onSubmit} />}
    </PageCard>
  )
}

export default ContactInformation
